// auth.ts — token storage + bearer injection + login/logout flow
//
// Session model:
//   - Access token stored in sessionStorage (per-tab; cleared on close)
//   - JSON envelope shape matches KF backend: {accessToken, tokenType}
//   - fetchWithAuth wraps fetch() to auto-inject Authorization header
//   - No refresh token flow yet (Wave B S3.1); expired token → 401 → forced logout
//
// Owner-visible flow:
//   1. Unauthenticated user sees LoginForm
//   2. Submits username+password → POST /api/auth/login
//   3. On 200: stores token, dispatches auth-changed event
//   4. Authenticated views appear; all fetches inject Bearer
//   5. Logout button clears token, dispatches auth-changed event

const TOKEN_KEY = 'keyforge.auth.token';
const TOKEN_TYPE_KEY = 'keyforge.auth.tokenType';
const AUTH_EVENT = 'keyforge-auth-changed';

export interface AuthToken {
  accessToken: string;
  tokenType: string;   // typically "Bearer"
}

// Why the auth state changed. Consumers (e.g. Toast component)
// decide UX based on this — a user-triggered logout is silent;
// a session-expired clear surfaces a "signed out" toast so the
// user isn't confused about being kicked to the login form.
export type AuthChangeReason =
  | 'login'           // storeToken() called
  | 'user-logout'     // logout() called
  | 'session-expired' // fetchWithAuth 401 auto-cleared
  | 'external';       // clearToken() with no reason (default)

export interface AuthChangeDetail {
  reason: AuthChangeReason;
}

export function getStoredToken(): AuthToken | null {
  const accessToken = sessionStorage.getItem(TOKEN_KEY);
  const tokenType = sessionStorage.getItem(TOKEN_TYPE_KEY);
  if (!accessToken || !tokenType) return null;
  return { accessToken, tokenType };
}

export function storeToken(token: AuthToken): void {
  sessionStorage.setItem(TOKEN_KEY, token.accessToken);
  sessionStorage.setItem(TOKEN_TYPE_KEY, token.tokenType);
  window.dispatchEvent(new CustomEvent<AuthChangeDetail>(AUTH_EVENT, { detail: { reason: 'login' } }));
}

export function clearToken(reason: AuthChangeReason = 'external'): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_TYPE_KEY);
  window.dispatchEvent(new CustomEvent<AuthChangeDetail>(AUTH_EVENT, { detail: { reason } }));
}

// Handler receives the CustomEvent so it can inspect .detail.reason.
// Overload keeps the existing () => void call sites working — the
// event arg is optional so old handlers ignore it.
export function onAuthChanged(handler: (evt: CustomEvent<AuthChangeDetail>) => void): () => void {
  const wrapped = handler as EventListener;
  window.addEventListener(AUTH_EVENT, wrapped);
  return () => window.removeEventListener(AUTH_EVENT, wrapped);
}

export function isAuthenticated(): boolean {
  return getStoredToken() !== null;
}

/**
 * fetch() wrapper that:
 *   1. Injects Authorization header from stored token
 *   2. Forces logout on 401 (token expired / invalidated)
 *   3. Otherwise transparent
 */
export async function fetchWithAuth(input: string, init?: RequestInit): Promise<Response> {
  const token = getStoredToken();
  const headers = new Headers(init?.headers);
  if (token) {
    headers.set('Authorization', `${token.tokenType} ${token.accessToken}`);
  }
  const res = await fetch(input, { ...init, headers });
  if (res.status === 401 && token) {
    // Token no longer valid — clear + notify with expiry reason so
    // subscribers can distinguish this from a user-triggered logout.
    clearToken('session-expired');
  }
  return res;
}

export type LoginResult =
  | { ok: true; token: AuthToken }
  | { ok: false; status: number; error: string; code?: string };

/**
 * POST /api/auth/login with { username, password }.
 * Stores token on success.
 */
export async function login(username: string, password: string): Promise<LoginResult> {
  try {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'accept': 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (res.ok) {
      const json = await res.json() as AuthToken;
      if (!json.accessToken || !json.tokenType) {
        return { ok: false, status: res.status, error: 'malformed token response' };
      }
      storeToken(json);
      return { ok: true, token: json };
    }
    // Try to extract structured error {error, code} from KF backend
    let error = res.statusText;
    let code: string | undefined;
    try {
      const errJson = await res.json() as { error?: string; code?: string };
      if (errJson.error) error = errJson.error;
      if (errJson.code) code = errJson.code;
    } catch { /* body not JSON — fall through with statusText */ }
    return { ok: false, status: res.status, error, code };
  } catch (e) {
    return { ok: false, status: 0, error: (e as Error).message };
  }
}

export function logout(): void {
  clearToken('user-logout');
}
